<template>
    <div class="animated fadeIn">
        <table
            vue:is="kgr-table"
            class="kgr-table kgr-table--striped kgr-table--block data-table table-groups"
            searching="false"
            ref="table"
        >
            <thead>
                <tr class="kgr-table__offering">
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.customId') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.title') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ $t('offering.appointments') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.runtime') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.sports') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.offeringCategory') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.instructor') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.location') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('offering.bookings') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ $t('Status') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="offering in offerings.filter(offering => matchesFilter(offering))"
                    :key="offering.id"
                    class="kgr-table__row kgr-table__offering kgr-table__row--top"
                >
                    <td class="kgr-table__cell">
                        {{ offering.customId ? offering.customId : '-' }}
                    </td>
                    <td class="kgr-table__cell">
                        <span v-if="offering.isEditable">
                            <a
                                :href="offeringUrlView(offering)"
                                class="kgr-link"
                            >
                                {{ offering.title }}
                            </a>
                            <a
                                class="kgr-table__action"
                                :href="offeringUrlEdit(offering)"
                                style="position: relative; top: 5px; right: -10px;"
                            >
                                <kgr-icon
                                    icon-class="edit"
                                    small
                                />
                            </a>
                        </span>
                        <div v-else>
                            {{ offering.title }}
                        </div>
                    </td>
                    <td
                        class="kgr-table__cell"
                        v-html="getAllWeekdays(offering.appointmentSeriesList)"
                    ></td>
                    <td
                        class="kgr-table__cell"
                        :data-order="getRuntimeStartOrder(offering.appointmentSeriesList)"
                    >
                        {{ getRuntime(offering.appointmentSeriesList) }}
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="offering.sport">
                            {{ offering.sport.name }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="offering.offeringCategory">
                            <span>{{ getCategoryName(offering.offeringCategory.name) }}</span><br />
                            <span>({{ getOfferingMode(offering) }})</span>
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        {{ getInstructorNames(offering.instructors) }}
                    </td>
                    <td class="kgr-table__cell kgr-text-align-top">
                        <div
                            v-for="field in getLocationNamesForFields(offering.fields)"
                            :key="field.name"
                            class="kgr-float kgr-float--left kgr-float--inText"
                        >
                            {{ field.name }}<br />
                            <span
                                class="kgr-text kgr-text--small"
                                style="font-size: 10px;"
                            >{{ field.location }}</span>
                        </div>
                    </td>
                    <td
                        class="kgr-table__cell"
                        style="min-width: 3em;"
                        :data-order="offering.nextAppointment.bookingCount"
                    >
                        <div :title="getNextAppointmentTitle(offering)">
                            <span
                                v-if="offering.nextAppointment.start"
                                class="kgr-text"
                                :class="getNextAppointmentColorClass(offering.nextAppointment)"
                            >
                                <span>{{ offering.nextAppointment.bookingCount }}<span v-if="offering.useWaitList"> (+{{ offering.nextAppointment.waitListCount }})</span></span> /
                                <span v-if="offering.nextAppointment.maxParticipants">{{ offering.nextAppointment.maxParticipants }}</span>
                                <span v-else>-</span>
                            </span>
                            <span v-else>-/-</span>
                        </div>
                    </td>
                    <td
                        class="kgr-table__cell"
                        style="min-width: 6em;"
                    >
                        <kgr-icon
                            v-if="offering.isPublished()"
                            icon-class="visibility-on"
                            :title="$t('offering.published')"
                        />
                        <kgr-icon
                            v-else
                            icon-class="visibility-off"
                            :title="$t('offering.notPublished')"
                        />
                        <kgr-icon
                            v-if="offering.useWaitList"
                            icon-class="list"
                            :title="$t('offering.withWaitList')"
                        />
                        <kgr-icon
                            v-if="offering.registrationRequested && offering.isBookable"
                            icon-class="list-check"
                            :title="$t('offering.bookable')"
                        />
                        <kgr-icon
                            v-else-if="offering.registrationRequested && !offering.isBookable"
                            icon-class="list-check"
                            light
                            :title="bookableAt(offering)"
                        />
                        <kgr-icon
                            v-if="offering.archived"
                            icon-class="archive"
                            light
                            :title="$t('offering.archived')"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import Icon from '../common/icon/Icon.vue';
import Interval from '../../js/offering/Interval';
import Offering from '../../js/dto/offering/Offering';
import OfferingCategory from '../../js/dto/offering/OfferingCategory';
import moment from 'moment';

export default {
    name: 'KgrOfferingTable',
    components: {
        'kgr-icon': Icon,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        offerings: {
            type: Array,
            required: true,
        },
        filter: {
            type: String,
            required: false,
        },

    },
    data() {
        return {};
    },

    mounted() {
        window.setupDataTable(this.$refs.table);
    },

    methods: {
        getNextAppointmentColorClass(nextAppointment) {
            if (!nextAppointment.start) {
                return '';
            }
            if (!nextAppointment.bookingRatio) {
                return 'kgr-color--success';
            }
            if (nextAppointment.bookingRatio < 80) {
                return 'kgr-color--success';
            }
            if (nextAppointment.bookingRatio >= 100) {
                return 'kgr-color--error';
            }
            return 'kgr-color--warning';
        },
        getNextAppointmentTitle(offering) {
            if (offering.bookingMode === Offering.BOOKING_MODE_OFFERING) {
                return this.$t('offering.offeringBookingWorkload');
            }
            if (!offering.nextAppointment.start) {
                return this.$t('offering.noAppointments');
            }
            return this.$t('offering.appointmentBookingWorkload', { date: offering.nextAppointment.start });
        },
        matchesFilter(offering) {
            if (this.filter === null || this.filter === '') {
                return true;
            }

            const filter = this.filter.toLowerCase().trim();

            if (offering.title.toLowerCase().indexOf(filter) !== -1) {
                return true;
            }

            if (offering.description.toLowerCase().indexOf(filter) !== -1) {
                return true;
            }

            if (offering.sports.name.toLowerCase().indexOf(filter) !== -1) {
                return true;
            }

            if (offering.offeringCategory.name.toLowerCase().indexOf(filter) !== -1) {
                return true;
            }
            return false;
        },
        getInstructorNames(instructors) {
            return instructors.map((instructor) => instructor.fullName).join(', ');
        },
        getCategoryName(categoryName) {
            if (OfferingCategory.isDefault(categoryName)) {
                return this.$t(`offering.${categoryName}`);
            }
            return categoryName;
        },
        getLocationNamesForFields(fields) {
            const fieldNames = [];
            if (fields.length !== 0) {
                fields.forEach((field) => {
                    fieldNames.push(
                        {
                            name: field.name,
                            location: field.locationName,
                        },
                    );
                });
            }
            return fieldNames;
        },
        getOfferingMode(offering) {
            if (offering.bookingMode === Offering.BOOKING_MODE_OFFERING) {
                return this.$t('offering.bookingModeOptions.offeringShort');
            } else {
                return this.$t('offering.bookingModeOptions.appointment');
            }
        },
        getAllWeekdays(appointments) {
            const output = [];

            appointments.forEach((appointmentData) => {
                const intervalWeekdays = appointmentData.interval ? Interval.getWeekdays(appointmentData.interval.type) : null;
                if (intervalWeekdays) {
                    output.push(`${intervalWeekdays.map((weekday) => this.$t(`weekday_short_${weekday}`)).join(', ')} ab ${this.formatTime(appointmentData.start)} Uhr`);

                    return;
                }

                const weekdays = [];
                const isDailyInterval = appointmentData.interval && appointmentData.interval.type === 'daily';
                const start = moment(appointmentData.start, 'YYYY-MM-DD');
                const end = moment(appointmentData.end, 'YYYY-MM-DD');
                const date = start;
                let counter = 0;
                while (date.isSameOrBefore(end)) {
                    const translation = this.$t(`weekday_short_${date.isoWeekday()}`);
                    if (!weekdays.includes(translation)) {
                        weekdays.push(translation);
                    }
                    date.add(1, 'd');
                    if (++counter >= 7) {
                        break;
                    }
                }
                counter = 0;
                if (isDailyInterval) {
                    while (date.isSameOrBefore(appointmentData.intervalEnd)) {
                        const translation = this.$t(`weekday_short_${date.isoWeekday()}`);
                        if (!weekdays.includes(translation)) {
                            weekdays.push(translation);
                        }
                        date.add(1, 'd');
                        if (++counter >= 7) {
                            break;
                        }
                    }
                }
                output.push(`${weekdays.join(', ')} ab ${this.formatTime(appointmentData.start)} Uhr`);
            });

            return output.join(',<br/>');
        },
        getRuntime(appointments) {
            const start = this.getRuntimeStart(appointments);
            const end = this.getRuntimeEnd(appointments);
            if (start === null) {
                return ' - ';
            }
            if (end === null) {
                return `ab ${start}`;
            }
            if (start === end) {
                return `am ${start}`;
            }
            return `${start} - ${end}`;
        },
        getRuntimeStartOrder(appointments) {
            let start = null;
            appointments.forEach((appointment) => {
                const appointmentStart = moment(appointment.start, 'YYYY-MM-DD');
                if (start === null) {
                    start = appointmentStart;
                }
                if (start.isAfter(appointmentStart)) {
                    start = appointmentStart;
                }
            });
            return start !== null ? start.format('YYYY-MM-DD') : null;
        },
        getRuntimeStart(appointments) {
            let start = null;
            appointments.forEach((appointment) => {
                const appointmentStart = moment(appointment.start, 'YYYY-MM-DD');
                if (start === null) {
                    start = appointmentStart;
                }
                if (start.isAfter(appointmentStart)) {
                    start = appointmentStart;
                }
            });
            return start !== null ? start.format('DD.MM.YYYY') : null;
        },
        getRuntimeEnd(appointments) {
            let end = null;
            appointments.forEach((appointment) => {
                let appointmentEnd = null;
                if (appointment.intervalEnd !== null) {
                    appointmentEnd = moment(appointment.intervalEnd, 'YYYY-MM-DD');
                }
                if (appointment.interval === null && !moment(appointment.start, 'YYYY-MM-DD').isSame(moment(appointment.end, 'YYYY-MM-DD'))) {
                    appointmentEnd = moment(appointment.end, 'YYYY-MM-DD');
                }
                if (end === null) {
                    end = appointmentEnd;
                }
                if (end !== null && end.isBefore(appointmentEnd)) {
                    end = appointmentEnd;
                }
            });
            return end !== null ? end.format('DD.MM.YYYY') : null;
        },
        offeringUrlView(offering) {
            return this.offeringUrl(offering, 'view');
        },
        offeringUrlEdit(offering) {
            return this.offeringUrl(offering, 'edit');
        },
        offeringUrl(offering, type) {
            if (offering.offeringCategory && offering.offeringCategory.name === OfferingCategory.DEFAULT_COURSE) {
                return `/backend/clubs/${this.clubId}/course-offerings/${offering.id}/${type}`;
            } else if (offering.offeringCategory && offering.offeringCategory.name === OfferingCategory.DEFAULT_TRAINING) {
                return `/backend/clubs/${this.clubId}/training-offerings/${offering.id}/${type}`;
            } else if (offering.offeringCategory && offering.offeringCategory.name === OfferingCategory.DEFAULT_EXAM) {
                return `/backend/clubs/${this.clubId}/exam-offerings/${offering.id}/${type}`;
            } else {
                return `/backend/clubs/${this.clubId}/generic-offerings/${offering.id}/${type}`;
            }
        },
        bookableAt(offering) {
            if (offering.bookingStart) {
                return `${this.$t('offering.bookableStart')} ${moment(offering.bookingStart, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY')}`;
            }
        },
        formatTime(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
